<template>
    <div
        v-if="!$wait.is('loading-page')"
        class="m-enterprise"
    >
        <div class="m-enterprise__hero">
            <y-container>
                <y-hero :content="hero" />
            </y-container>
        </div>

        <div class="m-enterprise__consultation">
            <y-container>
                <y-consultation-form :content="hero" />
            </y-container>
        </div>

        <div class="m-enterprise__features">
            <y-container>
                <y-features :content="hero" />
            </y-container>
        </div>

        <div class="m-enterprise__videos">
            <y-container>
                <y-video-features :content="videoFeatures" />
                <y-case-study
                    v-if="exploreReal"
                    :case-studies-data="exploreReal"
                    class="enterprise-case-studies"
                />
            </y-container>
        </div>

        <div class="m-enterprise__solutions">
            <y-container>
                <y-solutions :content="solutions" />
            </y-container>
        </div>

        <div class="m-enterprise__looking-for">
            <y-container>
                <y-looking-for :content="lookingFor" />
            </y-container>
        </div>

        <div class="m-enterprise__slide-show">
            <y-container>
                <y-our-client-say
                    :content="slideShowContentAndCta"
                    :clients-say="slideShow"
                    title-tag="h3"
                />
            </y-container>
        </div>

        <div class="m-enterprise__next-level">
            <y-container>
                <y-next-level
                    hide-second-button
                    :content="slideShowContentAndCta"
                />
            </y-container>
        </div>
    </div>
</template>

<script>

    import PageMixin from '@groot-nodes/mixins/Page.js';

    // Helper
    import { removeSymbol } from '@/helpers/helpers';

    // Components
    import YHero from '@/modules/enterprise/components/Hero/Index';
    import YFeatures from '@/modules/enterprise/components/Features/Index';
    import YVideoFeatures from '@/modules/enterprise/components/VideoAndFeatures/Index';
    import YCaseStudy from '@components/CaseStudy/CaseStudy.vue';
    import YLookingFor from '@/modules/enterprise/components/LookingFor/Index';
    import YSolutions from '@/modules/enterprise/components/Solutions/Index';
    import YNextLevel from '@/modules/about/components/TakeNextStep/Index';
    import YConsultationForm from '@/modules/enterprise/components/ConsultationForm/Index.vue';
    import YOurClientSay from '@/components/OurClientsSay/Index';

    export default {

        name: 'EnterprisePage',

        components: {
            YHero, YFeatures, YVideoFeatures, YCaseStudy, YLookingFor, YSolutions, YNextLevel, YConsultationForm, YOurClientSay,
        },

        mixins: [PageMixin],

        /**
         * @inheritDoc
         */
        data() {
            return {
                hero                  : '',
                videoFeatures         : {},
                exploreReal           : {},
                lookingFor            : {},
                solutions             : {},
                slideShowContentAndCta: {},
                slideShow             : null,
            };
        },

        /**
         * @inheritDoc
         */
        metaInfo() {
            return {
                title: this.hero.seo_post_title,

                meta: [
                    {
                        vmid   : 'description',
                        name   : 'description',
                        content: this.hero.seo_meta_description,
                    },
                ],
            };
        },

        methods: {
            removeSymbol,

            /**
             * Fetch home data
             *
             * @returns {Promise}
             */
            fetch() {
                const hero = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'enterprise1',
                    including: ['title', 'synopsis', 'image', 'text', 'custom', 'slug', 'albums', 'title2', 'seo_canonical', 'seo_meta_description', 'seo_post_title'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const videoFeatures = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'enterprise2',
                    including: ['title', 'synopsis', 'image', 'text', 'custom', 'slug', 'albums', 'title2'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const exploreReal = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'enterprise3',
                    including: ['title', 'custom', 'slug'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const exploreRealAlbum = this.$services.Content.postList({
                    type     : 'case-studies',
                    tagging  : 'enterprise',
                    including: ['title', 'synopsis', 'image', 'custom', 'url_encode', 'albums', 'slug'],
                    locale   : this.$i18n.locale,
                    limit    : 3,
                }).catch((error) => {
                    this.handleError(error);
                });

                const lookingFor = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'enterprise5',
                    including: ['title', 'synopsis', 'image', 'text', 'custom', 'slug', 'albums', 'title2'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const solutions = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'enterprise4',
                    including: ['title', 'synopsis', 'image', 'text', 'custom', 'slug', 'albums', 'title2'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const slideShow = this.$services.Content.postList({
                    type     : 'testimonials',
                    tagging  : 'enterprise',
                    including: ['title', 'title2', 'header_image', 'image', 'synopsis'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                const slideShowContentAndCta = this.$services.Content.postSingle({
                    type     : 'landing-page',
                    slug     : 'enterprise6',
                    including: ['image', 'text', 'title', 'custom'],
                    locale   : this.$i18n.locale,
                }).catch((error) => {
                    this.handleError(error);
                });

                return Promise.all(
                    [hero, videoFeatures, exploreReal, lookingFor, solutions, slideShow, slideShowContentAndCta, exploreRealAlbum],
                ).then((responses) => {
                    if (this.handleReq(responses, 0)) {
                        this.hero = responses[0].data.results;
                        this.heroContent = this.hero;
                        this.graphImage = this.heroContent.image;
                    }
                    if (this.handleReq(responses, 1)) {
                        this.videoFeatures = responses[1].data.results;
                    }
                    if (this.handleReq(responses, 2)) {
                        this.exploreReal = {
                            ...this.exploreReal,
                            ...responses[2].data.results,
                        };
                    }
                    if (this.handleReq(responses, 3)) {
                        this.lookingFor = responses[3].data.results;
                    }
                    if (this.handleReq(responses, 4)) {
                        this.solutions = responses[4].data.results;
                    }
                    if (this.handleReq(responses, 5)) {
                        this.slideShow = responses[5].data.results;
                    }
                    if (this.handleReq(responses, 6)) {
                        this.slideShowContentAndCta = responses[6].data.results;
                    }
                    if (this.handleReq(responses, 7)) {
                        this.exploreReal = {
                            ...this.exploreReal,
                            albums: responses[7].data.results.map((item) => ({
                                title      : item.title,
                                url_encode : item.url_encode,
                                description: item.synopsis,
                                slug       : item.slug,
                                ...item.image,
                            })),
                        };
                    }
                }).catch((error) => {
                    this.handleError(error);
                });
            },
        },

    };
</script>

<style lang="scss" src="../scss/enterprise.scss" />

